<script lang="ts" module>
	import drinkingCoffeeXs from "@assets/images/fathers-club-drinking-cofee.jpg?webp&h=480&imagetools";
	import drinkingCoffeeSm from "@assets/images/fathers-club-drinking-cofee.jpg?webp&h=640&imagetools";
	import drinkingCoffeeLg from "@assets/images/fathers-club-drinking-cofee.jpg?webp&h=800&imagetools";
	import drinkingCoffee2xl from "@assets/images/fathers-club-drinking-cofee.jpg?webp&imagetools";
	import heroImageXs from "@assets/images/fathers-family-club-hero.jpg?webp&h=1000&quality=95&imagetools";
	import heroImageSm from "@assets/images/fathers-family-club-hero.jpg?webp&h=1300&quality=95&imagetools";
	import heroImageLg from "@assets/images/fathers-family-club-hero.jpg?webp&h=1600&imagetools";
	import heroImageXl from "@assets/images/fathers-family-club-hero.jpg?webp&h=1900&imagetools";
	import heroImage3xl from "@assets/images/fathers-family-club-hero.jpg?webp&imagetools";
	import lastImageXs from "@assets/images/fathers-family-club-last.jpg?webp&h=1000&imagetools";
	import lastImageSm from "@assets/images/fathers-family-club-last.jpg?webp&h=1300&imagetools";
	import lastImageLg from "@assets/images/fathers-family-club-last.jpg?webp&h=1600&imagetools";
	import lastImage3xl from "@assets/images/fathers-family-club-last.jpg?webp&imagetools";
	import clubLogo from "@assets/images/fathers-family-club.png?webp&w=600&imagetools";
	import BuildYourSubscriptionButton from "@components/BuildYourSubscriptionButton.svelte";
	import SubscriptionSideModal from "@components/SubscriptionSideModal.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import { ButtonVariant } from "@components/common/ButtonVariant.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Link from "@components/common/Link.svelte";
	import Media from "@components/common/Media.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import AboutUsContentSection from "@components/sections/AboutUsContentSection.svelte";
	import BenefitsStrip from "@components/sections/BenefitsStrip.svelte";
	import FathersClubAdvantagesSection from "@components/sections/FathersClubAdvantagesSection.svelte";
	import FathersClubBenefitsSection from "@components/sections/FathersClubBenefitsSection.svelte";
	import FathersClubFaqSection from "@components/sections/FathersClubFaqSection.svelte";
	import FathersClubHowItWorksSection from "@components/sections/FathersClubHowItWorksSection.svelte";
	import FathersClubPricingSection from "@components/sections/FathersClubPricingSection.svelte";
	import FathersClubSubscriptionTypesSection from "@components/sections/FathersClubSubscriptionTypesSection.svelte";
	import ImageSection from "@components/sections/ImageSection.svelte";
	import { setConfiguratorState } from "@utils/configuratorContext.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import { getHowItWorksAnchor } from "@core/schema/paths/getHowItWorksAnchor.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./FathersClub.translations.json" with { type: "json" };
</script>

<script lang="ts">
	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);

	const configuratorState = $state({ open: false });
	setConfiguratorState(configuratorState);

	const iphoneSafePaddingBottom = tw("pb-[max(env(safe-area-inset-bottom,0),0.5rem)]");
	const heroImageHeight = tw("!h-[max(calc(100vh-8rem),32rem)]");
</script>

<Layout transparentHeader>
	<ImageSection
		lcp
		sources={{
			default: heroImageXs,
			">sm": heroImageSm,
			">lg": heroImageLg,
			">xl": heroImageXl,
			">3xl": heroImage3xl,
		}}
		darkOverlay
		class={[heroImageHeight, iphoneSafePaddingBottom]}
	>
		{#snippet heading()}
			<Heading
				level={1}
				text={translate("heroTitle")}
				class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
			>
				<Media
					width={325}
					height={305}
					sourcesets={{}}
					class="md:min-w-70"
					source={{ type: "image/webp", src: clubLogo }}
					alt={translate("heroTitle")}
				/>
			</Heading>
		{/snippet}
		{#snippet actions()}
			<BuildYourSubscriptionButton variant={ButtonVariant.OutlinedWhite} />
			<Link href={getHowItWorksAnchor(tenant.locale)} size={ButtonSize.Medium} variant={ButtonVariant.White}>
				<Formatted text={translate("howItWorks")} />
			</Link>
		{/snippet}
	</ImageSection>
	<FathersClubBenefitsSection />
	<section class="py-14 sm:py-17 md:py-20 text-center container-6">
		<Heading
			level={2}
			text={translate("title")}
			class="font-antipol font-medium text-8/tighter text-fcrdarkgray mb-8"
		/>
		<p class="text-sm/tight sm:text-lg/normal">
			<Formatted text={translate("description")} />
		</p>
	</section>
	<FathersClubHowItWorksSection />
	<AboutUsContentSection imageLeft={false} mediaClass="xl:w-150 xl:h-192">
		{#snippet heading()}
			<div class="flex flex-col">
				<div class="uppercase text-fcrorange font-bold tracking-[.1em]">
					<Formatted text={translate("regularitySection")} />
				</div>
				<Heading
					level={2}
					text={translate("regularityTitle")}
					class="font-antipol font-medium text-8/tighter text-fcrdarkgray mt-4"
				/>
			</div>
		{/snippet}
		<div class="flex flex-col h-full gap-8 lg:gap-16 xl:gap-18 2xl:gap-24">
			<p class="text-5/tighter mt-4">
				<Formatted text={translate("regularityParagraph")} />
			</p>
			<p class="font-antipol font-medium sm:text-8/tighter text-fcrdarkgray mt-10 sm:mt-14 xl:mt-auto">
				<Formatted text={translate("regularityTagline")} />
			</p>
		</div>
		{#snippet media()}
			<div class="absolute inset-0 flex flex-col justify-end items-center pb-8 sm:pb-12">
				<Media
					width={372}
					height={560}
					source={{ type: "image/webp", src: drinkingCoffeeXs }}
					sourcesets={{
						">sm": drinkingCoffeeSm,
						">lg": drinkingCoffeeLg,
						">2xl": drinkingCoffee2xl,
					}}
					alt=""
					class="size-full object-cover"
					wrapperClass="absolute inset-0"
				/>
				<BuildYourSubscriptionButton variant={ButtonVariant.OutlinedWhite} />
			</div>
		{/snippet}
	</AboutUsContentSection>
	<FathersClubAdvantagesSection />
	<FathersClubFaqSection />
	<FathersClubPricingSection />
	<FathersClubSubscriptionTypesSection />
	<BenefitsStrip items={[translate("regularityTagline")]} />
	<ImageSection
		sources={{
			default: lastImageXs,
			">sm": lastImageSm,
			">lg": lastImageLg,
			">3xl": lastImage3xl,
		}}
		darkOverlay
		class={[heroImageHeight]}
	>
		{#snippet heading()}
			<Heading
				level={2}
				text={translate("heroTitle")}
				class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
			>
				<Media
					width={325}
					height={305}
					sourcesets={{}}
					source={{ type: "image/webp", src: clubLogo }}
					alt={translate("heroTitle")}
				/>
			</Heading>
		{/snippet}
		{#snippet actions()}
			<BuildYourSubscriptionButton variant={ButtonVariant.OutlinedWhite} />
		{/snippet}
	</ImageSection>
	<div
		class={[
			"flex justify-center sm:justify-end items-center",
			"pointer-events-none inset-x-0 fixed bottom-0 z-40 gap-4 px-4 sm:px-6",
			iphoneSafePaddingBottom,
		]}
	>
		<BuildYourSubscriptionButton class="w-full sm:w-fit" variant={ButtonVariant.Dark} />
	</div>
	<SubscriptionSideModal bind:show={configuratorState.open} />
</Layout>
